<template>
  <v-dialog 
    v-model="open" 
    content-class="popup-container" 
    max-width="50vw" 
    @click:outside="() => $emit('exit')"
  >
    <v-card class="mx-auto set-layout">

      <v-row>
        <v-col cols="4">
          <div class="org-select-item">
            <span class="label">Organisation</span>
            <v-autocomplete
              v-model="org"
              label="Select Organisation"
              :items="orgList"
              item-text="name"
              item-value="id"
              :item-disabled="(j) => j.disabled === 'True'"
              style="height:10%"
              solo
              required
            ></v-autocomplete>
          </div>
        </v-col>
      </v-row>
      
      <v-container fluid style="height:80%">
        <v-row justify="space-between" align="center" class="fill-height">
          <v-col cols="5" class="fill-height">
            <v-text-field
              v-if="org"
              v-model="search"
              label="Search Vehicles"
              prepend-icon="mdi-magnify"
            ></v-text-field>
            <v-card v-if="org == 'Unassigned'" class="transfer-source mx-auto" outlined tile>
              <v-data-table
                :headers="tableHeaders"
                :items="filteredVehicles"
                disable-pagination
                :hide-default-footer="true"
                item-key="id"
                :loading="loader"
                @click:row="rowClick"  
              >
              </v-data-table>
            </v-card>
            <v-card v-else class="transfer-source mx-auto" outlined tile>
              <div class="fill-height">
                <v-progress-circular
                  v-if="loader"
                  :size="50"
                  color="secondary"
                  class="progress-circular-absolute"
                  indeterminate
                ></v-progress-circular>
                <v-list class="fill-height pa-0">
                  <v-list-item-group v-model="selectedVehicles" multiple class="fill-height vehicle-list-scroll">
                    <div  v-for="(car, j) in filteredVehicles" :key="'vehi' + j">    
                      <v-tooltip right :disabled="!checkCompatibility(car)">
                        <template v-slot:activator="{on}">   
                          <div v-on="on">
                            <v-list-item  
                              :disabled="checkCompatibility(car)" 
                              :value="car" 
                              active-class="third--text"
                            >
                              <v-list-item-icon>
                                <v-icon>mdi-car</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title v-text="car.vehicle_name"></v-list-item-title>
                              </v-list-item-content>       
                            </v-list-item>
                          </div>            
                        </template>
                        <span>{{incompatibleWarning(car)}}</span>
                      </v-tooltip>
                    </div>
                  </v-list-item-group>
                </v-list>
              </div>
            </v-card>
          </v-col>
          <v-col cols="2" class="d-flex fill-height align-center justify-center flex-column text-center">
            <v-btn :disabled="selectedVehicles.length < 1" class="ma-2" outlined large fab color="third" @click="assign">
              <v-icon>mdi-chevron-double-right</v-icon>
            </v-btn>
            <v-btn :disabled="selectedDevices.length < 1" class="ma-2" outlined large fab color="third" @click="unassign">
              <v-icon>mdi-chevron-double-left</v-icon>
            </v-btn>
            <v-btn :disabled="!(org && vehicles.length > 0)" class="ma-2" outlined large fab color="warning" @click="assignAll">
              <v-icon>mdi-chevron-triple-right</v-icon>
            </v-btn>
            <v-btn :disabled="!(org && vehicles.length > 0)" class="ma-2" outlined large fab color="warning" @click="deselectAll">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="5" class="fill-height">
            <v-card class="mx-auto pa-2 transfer-target" outlined tile>
              <v-subheader>Target Devices</v-subheader>
              <div class="fill-height">
                <v-card v-if="org == 'Unassigned'" class="transfer-source mx-auto" outlined tile>
                  <v-data-table
                    :headers="tableHeaders"
                    :items="devices"
                    disable-pagination
                    :hide-default-footer="true"
                    item-key="id"
                  />
                </v-card>
                <v-list v-else class="pa-0 devices-scroll">
                  <v-list-item-group v-model="selectedDevices" multiple>
                    <template v-for="device in devices">
                      <v-list-item :key="device.id" :value="device" active-class="third--text">
                        <template>
                          <v-list-item-content>
                            <v-list-item-title v-text="device.vehicle_name"></v-list-item-title>
                          </v-list-item-content>
                        </template>
                      </v-list-item>
                    </template>
                  </v-list-item-group>
                </v-list>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="confirm">OK</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { GetOrgVehicles, GetUnassignedDevices, GetOrgDevices } from "@/services/API.js"
import { mapState } from "vuex"

import { Logger } from "aws-amplify"
const logger = new Logger("Device Selector")

export default {
  name: 'DeviceSelector',
  props: {
    open: {
      required: true,
      type: Boolean,
    },
    value: {
      required: true,
      type: Array,
    },
    deviceModel: {
      required: true,
      type: String
    },
    tableHeaders: {
      required: true,
      type: Array
    },
    compatibilityLimit: {
      required: false,
      default: () => {},
      type: Object
    },
    configOptionsVersionMap: {
      type: Object,
      required: true
    },
    supportsPartialConfig: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    selectedDevices: [],
    selectedVehicles: [],
    org: null,
    vehicles: [],
    devices: [],
    orgDevices: [],
    loader: false,
    search: '',
    deviceModels: ['All'],
    selectedModel: 'All',
    items: [],
    orgList: [],
    incompatibleMessage: ''
  }),
  computed: {
    ...mapState(["orgs"]),
    filteredVehicles() {
      if (this.search.length > 0) {
        let filteredItems = this.vehicles.filter(item => {
          return item.vehicle_name.toLowerCase().includes(this.search.toLowerCase());
        });

        return filteredItems;
      }
      else {
        return this.vehicles;
      }
    }
  },
  watch: {
    org(val) {
      if (val) {
        this.clearAll();
        this.getOrgVehicles(val)
      }
    },
    open(val) {
      if (val) {
        this.devices = this.value
      }
    },
    devices(val) {
      if (val) {
        this.$emit("input", this.devices)
      }
    }
  },
  mounted() {
    this.compileOrgList();
  },
  methods: {
    incompatibleWarning(item) {
      const deviceId = item.devices.find(device => device.is_defaultdevice == true);
      const currentDevice = this.orgDevices.find(device => device.id == deviceId.device_id);

      if (this.supportsPartialConfig) {
        const abovePartialConfigLimit = this.checkIfVersionIsAboveLimit(currentDevice.firmware, this.configOptionsVersionMap[currentDevice.model]['to']);
        const belowPartialConfigLimit = this.checkIfVersionIsAboveLimit(this.configOptionsVersionMap[currentDevice.model]['from'], currentDevice.firmware);
        const greaterThanFWLimit = this.checkIfVersionIsAboveLimit(currentDevice.firmware, this.compatibilityLimit.to);
        const greaterThanFWBase = this.checkIfVersionIsAboveLimit(this.compatibilityLimit.from, currentDevice.firmware)

        
        if(abovePartialConfigLimit || belowPartialConfigLimit) {
          return `Only supported by firmware version ${this.configOptionsVersionMap[currentDevice.model]['from']} and above`;
        } 
        else if (greaterThanFWLimit || greaterThanFWBase) {
          return `Only supported by firmware version ${this.compatibilityLimit.from} and above`;
        }
      } else {
        const greaterThanLimit = this.checkIfVersionIsAboveLimit(currentDevice.firmware, this.compatibilityLimit.to);
        const greaterThanBase = this.checkIfVersionIsAboveLimit(this.compatibilityLimit.from, currentDevice.firmware)

        if (greaterThanLimit) {
          return `Incompatible firmware - this config is only compatible with firmware versions ${this.compatibilityLimit.to} and below`;
        }
        if (greaterThanBase) {
          return `Incompatible firmware - firmware version ${this.compatibilityLimit.from} or higher required`;
        }
      }
    },  
    checkCompatibility(item, unassigned = false) {
      let deviceId = '';
      let currentDevice = {};

      if(unassigned) {
        deviceId = item.id;
        currentDevice = this.vehicles.find(device => device.id == deviceId);
      } else {
        deviceId = item.devices.find(device => device.is_defaultdevice == true);
        currentDevice = this.orgDevices.find(device => device.id == deviceId.device_id);
        logger.info('currentDevice info ', currentDevice)
      }

      if (!currentDevice) return true;

      if (this.supportsPartialConfig) {
        const abovePartialConfigLimit = this.checkIfVersionIsAboveLimit(currentDevice.firmware, this.configOptionsVersionMap[currentDevice.model]['to']);
        const belowPartialConfigLimit = this.checkIfVersionIsAboveLimit(this.configOptionsVersionMap[currentDevice.model]['from'], currentDevice.firmware);
        const greaterThanFWLimit = this.checkIfVersionIsAboveLimit(currentDevice.firmware, this.compatibilityLimit.to);
        const greaterThanFWBase = this.checkIfVersionIsAboveLimit(this.compatibilityLimit.from, currentDevice.firmware)

        
        if(abovePartialConfigLimit || belowPartialConfigLimit) {
          // this.supportsPartialConfig = true;
          // this.setInitconfigType();
          // logger.info('is true ', this.supportsPartialConfig)
          logger.info('partial above below check ',abovePartialConfigLimit, belowPartialConfigLimit)
          this.incompatibleMessage = `Only supported by firmware version ${this.configOptionsVersionMap[currentDevice.model]['from']} and above`
          return true;
        } 
        else if (greaterThanFWLimit || greaterThanFWBase) {
          this.incompatibleMessage = `Only supported by firmware version ${this.compatibilityLimit.from} and above`
          return true;
        }
        else {
          return false;
        }
      }
      else {
        logger.info('##########')
        logger.info(currentDevice)
        const greaterThanFWLimit = this.checkIfVersionIsAboveLimit(currentDevice.firmware, this.compatibilityLimit.to);
        const greaterThanFWBase = this.checkIfVersionIsAboveLimit(this.compatibilityLimit.from, currentDevice.firmware)

        if (greaterThanFWLimit || greaterThanFWBase) {
          this.incompatibleMessage = `Only supported by firmware version ${this.compatibilityLimit.from} and above`
          return true
        }
        else {
          return false;
        }
      }
      
    },  

    checkIfVersionIsAboveLimit (current, limit) {
      // When called during Device Config job creation, there won't be a limit so just return false to continue with the job
      if (limit == '') return false;
      const _current = current.split('.');
      const _limit = limit.split('.');

      for (var i = 0; i < _current.length; i++) {
        const a = ~~_current[i];
        const b = ~~_limit[i];

        if (a > b) return true;
        if (a < b) return false;
      }

      return false;
    },

    rowClick(item, row) {      
      const incompatible = this.checkCompatibility(item, true);

      if (incompatible) return;

      row.select(true);
      let index = this.selectedVehicles.indexOf(item);

      if (index > -1) {
        this.selectedVehicles.splice(index, 1);
      } else {
        this.selectedVehicles.push(item);
      }
    },
    compileOrgList() {
      let sortedOrgs = this.orgs.sort();
      let orgObj = {
        name: 'Unassigned',
        disabled: false
      }

      this.orgList[0] = orgObj;
      this.orgList.push(...sortedOrgs);
    },
    clearAll() {
      this.selectedVehicles = [];
      this.devices = [];
      this.selectedDevices = [];
    },
    deselectAll() {
      this.selectedVehicles = [];
    },
    assign() {
      if (this.org != 'Unassigned') {
        this.devices = this.devices.concat(this.selectedVehicles.filter((vehi) => !this.devices.map((dev) => dev.id).includes(vehi.id)))
      }
      else {
        this.devices = this.selectedVehicles;
      } 
      
      this.selectedVehicles = [];
    },
    unassign() {
      this.devices = this.devices.filter((vehi) => !this.selectedDevices.map((dev) => dev.id).includes(vehi.id))
      this.selectedDevices = []
    },
    assignAll() {
      this.devices = this.devices.concat(this.vehicles.filter((vehi) => !this.devices.map((dev) => dev.id).includes(vehi.id)))
      this.selectedVehicles = []
    },
    async getOrgVehicles(org) {
      if (org != 'Unassigned') {
        this.loader = true
        const vehicles = await GetOrgVehicles(org, true)
        const devices = await this.batchDeviceRequest(org);

        if (vehicles.length > 0) {
          this.vehicles = vehicles.filter((car) => car.devices && car.devices.length > 0 && car.devices.every(device => device.model === this.deviceModel));
        }
        if (devices.Devices.length > 0) {
          const deviceIds = [];

          this.vehicles.map((vehicle) => {
             vehicle.devices.some((device) => {
              if (device.is_defaultdevice === true) {
                deviceIds.push(device.device_id);
              }
            });
          });
         
          this.orgDevices = devices.Devices.filter(device => deviceIds.includes(device.id));
        }
        this.loader = false
      }
      else {
        this.getUnassignedDevices(0);
      }
      
    },
    batchDeviceRequest (id) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async res => {
        try {
          // we need some arbitrary limit to prevent loading forever
          const maxPages = 10
          let currentPage = 0
          let more = true
          let fullList = []
          while(currentPage < maxPages && more) {
            let list = await GetOrgDevices(id, `page_num=${currentPage}&rows_per_page=1000&order_by=id`)
            if(list && list.Devices && Array.isArray(list.Devices) && list.Devices.length) {
              fullList = fullList.concat(list.Devices)
            } else {
              more = false
            }
            currentPage++
          }
          res({Devices: fullList})
        } catch {
          res([])
        }  
      }) 
    },
    async getUnassignedDevices(page, items = []) {
      this.loader = true;

      let set = items;
      let index = page;
      let devices = await GetUnassignedDevices(page)
      
      
      if(devices.Devices) {
        for (let device in devices.Devices) {
          set.push(devices.Devices[device]);
        }

        if (devices.Devices.length >= 50) {
          index++;

          this.getUnassignedDevices(index, set);
        } else {

          this.vehicles = set.filter(device => device.model == this.deviceModel);
          this.loader = false;
        }
      }
      else {
        this.loader = false;
      }
    },
    confirm() {
      this.$emit("exit", this.org)
    },
  },
}
</script>

<style lang="scss">
.popup-container {
  overflow: scroll;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #aaa;
    transition: all .2s;

    &:hover {
      background: #999;
    }
  }
}
.transfer-source {
  height: 90%;
  overflow: scroll;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #aaa;
    transition: all .2s;

    &:hover {
      background: #999;
    }
  }
}
.vehicle-list-scroll {
  overflow-x: hidden;
  overflow-y: auto;
}
.transfer-target {
  height: 100%;
  .devices-scroll {
    height: calc(100% - 48px);
    overflow: auto;
  }
}
.org-select-item {
  padding: 0 1em 0 1em;
  .label {
    font-size: 13px;
  }
}

tr.v-data-table__selected {
  background: #c1d4f5 !important;
}
.theme--light.v-data-table tbody tr.v-data-table__selected {
  background: #c1d4f5 !important;
}

.v-data-table__wrapper {
  overflow-x: hidden !important;
}

.set-layout {
  height: 85vh;
  width: 50vw;
  overflow-x: hidden;
}

</style>
